import { useContext } from 'react';

import { CurrentTenantContext } from '@/contexts/current-tenant-context';

import { PlanTier, TenantFeature } from '../types/tenant-features';

/**
 * Hook to check tenant entitlements (subscription features).
 *
 * Provides feature checks based on the tenant's subscription plan.
 * This is the tenant-level access control axis ("does the plan include this?").
 * Combine with useCurrentUser() for user-level access control ("does the user have permission?").
 *
 * Data source migration path:
 * - Phase 1 (current): Hardcoded in CurrentTenantContext
 * - Phase 2: Fetched from tenant-api /entitlement endpoint
 * - Phase 3: JWT claims from Zitadel via account-api
 *
 * @example
 * ```tsx
 * const { hasFeature, planTier } = useEntitlement();
 *
 * if (hasFeature(TenantFeature.ANALYTICS)) {
 *   // Show analytics
 * }
 * ```
 */
export function useEntitlement() {
  const context = useContext(CurrentTenantContext);
  const features = context?.currentTenant?.pricingPlan?.features ?? [];

  return {
    /** Check if a specific feature is available on the tenant's plan */
    hasFeature: (feature: TenantFeature): boolean => features.includes(feature),

    /** The current plan tier (derived from context, hardcoded in Phase 1) */
    planTier: (context?.currentTenant?.pricingPlan?.name as PlanTier) ?? null,

    /** Raw features array */
    features,
  };
}
